/* eslint-disable compat/compat */
// eslint-disable-next-line import/no-unresolved
import KeenSlider from 'https://cdn.skypack.dev/pin/keen-slider@v6.7.0-LKl0HtBlpKLwpmTQiIJc/mode=imports,min/optimized/keen-slider.js'

const { translateString } = window.BAO.Utils

class ProductRecommendationCarousel extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    window.addEventListener(this.dataset.event, e => {
      const products = e.detail.data
      if (!products || products.length === 0) return

      // Split products array into subsets based on availability
      let [productsToProcess, unavailableProducts] = this.partitionProducts(
        products,
        product => product.available === true,
      )

      const allUnavailable = !productsToProcess.length

      if (e.type !== 'productAdditionalColorsDataIsReady' && allUnavailable) {
        return
      }

      if (
        e.type === 'productAdditionalColorsDataIsReady' &&
        unavailableProducts.length > 0
      ) {
        // Append unavailable products to the end of the array
        productsToProcess = productsToProcess.concat(unavailableProducts)
      }

      const currentUrl = new URL(window.location)

      productsToProcess.forEach((product, index) => {
        const sectionUrl = new URL(product.__tagalys_fields.link)

        if (this.dataset.preferredImageGender) {
          sectionUrl.searchParams.set(
            'gender',
            this.dataset.preferredImageGender,
          )
        }

        const preferredImageGender =
          currentUrl.searchParams.get('gender') ||
          this.dataset.preferredImageGender
        if (preferredImageGender) {
          sectionUrl.searchParams.set('view', preferredImageGender)
        }

        const asyncCard = `<li data-product-recommendation-carousel-el="slide">
          <section-async
            class="prd-Card_Async"
            section-id="product-recommendation-card"
            url="${sectionUrl.toString()}"
            aria-busy="true"
          ></section-async>
        </li>`
        const dot = `
          <button
            type="button"
            data-product-recommendation-carousel-el="dot"
            class="prd-Detail_Dot"
          >
            <span class="util-ScreenReaderOnly">
              ${translateString(this.translations.view_image_current_of_total, {
                current: index + 1,
                total: productsToProcess.length,
              })}
            </span>
          </button>
        `

        this.els.slides.element.insertAdjacentHTML('beforeend', asyncCard)
        this.els.dots.element.insertAdjacentHTML('beforeend', dot)
      })

      this.els.slides.element.classList.add('keen-slider')
      this.els.slide.elements.forEach(el =>
        el.classList.add('keen-slider__slide'),
      )

      this.els.next.element.addEventListener('click', e => {
        this.carousel.next()
      })

      this.els.prev.element.addEventListener('click', e => {
        this.carousel.prev()
      })

      this.carousel = this.initialiseCarousel()

      window.addEventListener('initialiseCarousel', e => {
        setTimeout(() => {
          this.carousel.update()
        }, 50)
      })
    })
  }

  /**
   * @param {{
   *  next?: HTMLElement
   *  previous?: HTMLElement
   * }} arrows
   */
  useArrows ({ next, previous } = {}) {
    /** @param {KeenSliderInstance} carousel */
    return carousel => {
      next?.addEventListener('click', carousel.next)
      previous?.addEventListener('click', carousel.previous)

      const updateButtonStates = () => {
        const { abs, maxIdx, min } = carousel.track.details
        const shouldHide = String(maxIdx === 0)

        if (next) {
          next.disabled = abs === maxIdx
          next.setAttribute('aria-hidden', shouldHide)
        }

        if (previous) {
          previous.disabled = abs === min
          previous.setAttribute('aria-hidden', shouldHide)
        }
      }

      carousel.on('created', updateButtonStates)
      carousel.on('slideChanged', updateButtonStates)
      carousel.on('optionsChanged', updateButtonStates)
    }
  }

  useCurrentSlideIndicator = (indicator, template = '{{ current }}') => {
    /** @param {KeenSliderInstance} carousel */
    return carousel => {
      const updateIndicator = () => {
        const { rel, maxIdx } = carousel.track.details
        const current = String(rel + 1)
        const total = String(maxIdx + 1)
        indicator.textContent = window.BAO.Utils.translateString(template, {
          current,
          total,
        })
      }

      carousel.on('created', updateIndicator)
      carousel.on('slideChanged', updateIndicator)
      carousel.on('optionsChanged', updateIndicator)
    }
  }

  useDots = (dots = []) => {
    /** @param {KeenSliderInstance} carousel */
    return carousel => {
      const updateDotStates = () => {
        dots.forEach((dot, index) => {
          const isCurrent = carousel.track.details.rel === index

          dot.setAttribute('aria-current', String(isCurrent))
        })
      }

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          carousel.moveToIdx(index)
        })
      })

      carousel.on('created', updateDotStates)
      carousel.on('slideChanged', updateDotStates)
    }
  }

  /** @param {HTMLElement} indicator */
  toggleSlideIndicator (indicator) {
    /** @param {KeenSliderInstance} carousel */
    return carousel => {
      const update = () => {
        const { maxIdx } = carousel.track.details

        indicator.hidden = maxIdx === 0
      }

      carousel.on('created', update)
      carousel.on('slideChanged', update)
      carousel.on('optionsChanged', update)
    }
  }

  initialiseCarousel () {
    return new KeenSlider(
      this.els.slides.element,
      {
        breakpoints: {
          '(min-width: 768px)': {
            slides: { perView: 3, spacing: 11 },
          },
        },
        slides: {
          perView: 1.2,
          spacing: 10,
        },
      },
      [
        this.useCurrentSlideIndicator(
          this.els.totalPage.element,
          '{{ current }}/{{ total }}',
        ),
        this.useDots(this.els.dot.elements),
        this.toggleSlideIndicator(this.els.page.element),
        this.useArrows({
          next: this.els?.next.element,
          previous: this.els?.prev.element,
        }),
      ],
    )
  }

  partitionProducts (products, filter) {
    const available = []
    const unavailable = []

    products.forEach((e, i, arr) =>
      (filter(e, i, arr) ? available : unavailable).push(e),
    )

    return [available, unavailable]
  }

  get totalPages () {
    return Math.ceil(this.slideCount / 3)
  }

  get slideCount () {
    return this.els.slide.elements.length
  }

  /** @returns {Record<'view_image_current_of_total', string>} */
  get translations () {
    return JSON.parse(this.dataset.translations)
  }
}

if (!customElements.get('product-recommendation-carousel')) {
  customElements.define(
    'product-recommendation-carousel',
    ProductRecommendationCarousel,
  )
}
